/**
 * Parses Vite environment variables into a typed object the rest of the app
 * can trust. Anything missing is surfaced at boot, not three clicks deep.
 *
 * Keycloak config is *optional in dev*: when VITE_KC_ISSUER is unset we
 * skip the login flow entirely and let the backend's dev-user passthrough
 * handle auth.
 */
export interface WebEnv {
  apiBaseUrl: string;
  oidc: OidcEnv | null;
  sentryDsn: string | null;
}

export interface OidcEnv {
  issuer: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
}

function required(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(`[mrrmlab/web] Missing env var ${name}`);
  }
  return value;
}

export function loadEnv(): WebEnv {
  const env = import.meta.env;
  const apiBaseUrl = required('VITE_API_BASE_URL', env.VITE_API_BASE_URL);

  const issuer = env.VITE_KC_ISSUER?.trim();
  const oidc: OidcEnv | null = issuer
    ? {
        issuer,
        clientId: required('VITE_KC_CLIENT_ID', env.VITE_KC_CLIENT_ID),
        redirectUri: required('VITE_KC_REDIRECT_URI', env.VITE_KC_REDIRECT_URI),
        postLogoutRedirectUri: required(
          'VITE_KC_POST_LOGOUT_REDIRECT_URI',
          env.VITE_KC_POST_LOGOUT_REDIRECT_URI,
        ),
      }
    : null;

  const sentryDsn = env.VITE_SENTRY_DSN?.trim() || null;

  return { apiBaseUrl, oidc, sentryDsn };
}
